import React, { useCallback, useEffect, useState } from 'react';
import { ListChecks, RefreshCw } from 'lucide-react';
import { reconciliationApi, type ReconciliationProposal } from '@/services/api/agentApi';
import { ProposalCard } from './ProposalCard';
import { Button } from '@/components/ui/button';

const STATUS_TABS = [
  { value: 'pending_review', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Dismissed' },
] as const;

const TYPE_GROUPS: Array<{ key: string; label: string; types: ReconciliationProposal['proposal_type'][] }> = [
  { key: 'action', label: 'Needs action', types: ['overdue', 'forgotten_commitment', 'likely_complete', 'blocked'] },
  { key: 'attention', label: 'For your attention', types: ['stalled', 'cascade_ready'] },
  { key: 'new', label: 'New work', types: ['proposed_goal_or_task'] },
];

export const ReconciliationTab: React.FC = () => {
  const [scope, setScope] = useState<'mine' | 'org'>('mine');
  const [status, setStatus] = useState<string>('pending_review');
  const [items, setItems] = useState<ReconciliationProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reconciliationApi.list(scope, status);
      setItems(res.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [scope, status]);

  useEffect(() => { void refresh(); }, [refresh]);

  const onApprove = async (id: string) => { await reconciliationApi.approve(id); await refresh(); };
  const onReject = async (id: string) => { await reconciliationApi.reject(id); await refresh(); };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Task & Goal Proposals</h2>
          <span className="text-xs text-muted-foreground">
            What your agent noticed about your work
          </span>
        </div>
        <Button size="sm" variant="ghost" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="text-xs text-muted-foreground mr-2">Scope:</div>
        <Button size="sm" variant={scope === 'mine' ? 'default' : 'outline'} onClick={() => setScope('mine')}>
          Mine
        </Button>
        <Button size="sm" variant={scope === 'org' ? 'default' : 'outline'} onClick={() => setScope('org')}>
          Org
        </Button>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b">
        {STATUS_TABS.map(t => (
          <button
            key={t.value}
            onClick={() => setStatus(t.value)}
            className={`px-3 py-1.5 text-sm border-b-2 transition-colors ${
              status === t.value
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="text-sm text-red-600 mb-3">Failed to load: {error}</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-muted-foreground py-12 text-center">
          No proposals in this view.
        </div>
      )}

      {TYPE_GROUPS.map(group => {
        const groupItems = items.filter(i => group.types.includes(i.proposal_type));
        if (groupItems.length === 0) return null;
        return (
          <div key={group.key} className="mb-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">{group.label}</h3>
            {groupItems.map(p => (
              <ProposalCard key={p.proposal_id} proposal={p} onApprove={onApprove} onReject={onReject} />
            ))}
          </div>
        );
      })}
    </div>
  );
};
